import { useEffect, useMemo, useState } from 'react'
import { Head, router, useForm } from '@inertiajs/react'
import Layout from '../components/Layout'
import useLiveRefresh from '../use_live_refresh'

// Poll for entries logged on the other parent's phone; the "when" default is left alone so a
// refresh can't rewrite a form that's halfway typed.
const REFRESHED_PROPS = ['entries', 'summary']

const ENDPOINTS = { feeding: '/feedings', diaper: '/diaper_changes' }

function Stat({ emoji, value, label }) {
  return (
    <div className="card-cute flex items-center gap-2 px-2.5 py-2 sm:gap-3 sm:px-4 sm:py-3">
      <span className="text-lg sm:text-2xl" aria-hidden="true">
        {emoji}
      </span>
      <div>
        <p className="font-display text-base leading-none font-extrabold text-ink sm:text-xl">{value}</p>
        <p className="text-[10px] font-bold tracking-wide text-ink-soft uppercase sm:text-xs">{label}</p>
      </div>
    </div>
  )
}

function KindPill({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`shrink-0 cursor-pointer rounded-full border-2 px-3 py-2 font-display text-sm font-bold whitespace-nowrap transition ${
        active
          ? 'border-blush-400 bg-blush-400 text-white shadow'
          : 'border-blush-100 bg-white/80 text-ink-soft hover:bg-white'
      }`}
    >
      {children}
    </button>
  )
}

function FieldError({ message }) {
  if (!message) return null
  return <p className="mt-1 text-sm font-bold text-rose-600">{message}</p>
}

// One entry, being written down or corrected. `entry` is null when logging a fresh one, in which
// case the time is left blank and the server stamps it — so a form left open on the counter still
// logs the moment you tap save, not the moment the page loaded.
function EntryForm({ type, kinds, entry, now, formId, activeForm, onSubmitting, onDone }) {
  const editing = Boolean(entry)
  const feeding = type === 'feeding'

  const { data, setData, post, patch, processing, errors, reset } = useForm({
    kind: entry?.kind || kinds[0].value,
    occurred_at: entry?.occurredAtLocal || '',
    amount_oz: entry?.amountOz ?? '',
    note: entry?.note || '',
  })

  // Errors arrive as one shared bag, so only the form that sent the request shows them.
  const shown = activeForm === formId ? errors : {}
  const [pickingTime, setPickingTime] = useState(editing)

  function submit(event) {
    event.preventDefault()
    onSubmitting(formId)

    const options = {
      preserveScroll: true,
      onSuccess: () => {
        // A quick-log card stays put and clears itself, ready for the next one.
        if (!editing) {
          reset()
          setPickingTime(false)
        }
        onDone()
      },
    }

    if (editing) {
      patch(`${ENDPOINTS[type]}/${entry.id}`, options)
    } else {
      post(ENDPOINTS[type], options)
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {kinds.map((kind) => (
          <KindPill key={kind.value} active={data.kind === kind.value} onClick={() => setData('kind', kind.value)}>
            <span aria-hidden="true">{kind.emoji}</span> {kind.label}
          </KindPill>
        ))}
      </div>
      <FieldError message={shown.kind} />

      <div className={`grid gap-4 ${feeding ? 'sm:grid-cols-2' : ''}`}>
        {feeding && (
          <div>
            <label htmlFor={`${formId}-amount`} className="mb-1.5 block font-display font-bold text-ink-soft">
              Ounces <span className="font-normal">(optional)</span>
            </label>
            <input
              id={`${formId}-amount`}
              type="number"
              inputMode="decimal"
              step="0.5"
              min="0"
              max="60"
              value={data.amount_oz}
              onChange={(event) => setData('amount_oz', event.target.value)}
              placeholder="4"
              className="input-cute"
            />
            <FieldError message={shown.amount_oz} />
          </div>
        )}

        <div>
          <label htmlFor={`${formId}-note`} className="mb-1.5 block font-display font-bold text-ink-soft">
            Note <span className="font-normal">(optional)</span>
          </label>
          <input
            id={`${formId}-note`}
            type="text"
            value={data.note}
            onChange={(event) => setData('note', event.target.value)}
            maxLength={200}
            placeholder={feeding ? 'Fell asleep halfway' : 'Bit of a blowout'}
            className="input-cute"
          />
          <FieldError message={shown.note} />
        </div>
      </div>

      <div>
        {pickingTime ? (
          <>
            <label htmlFor={`${formId}-when`} className="mb-1.5 block font-display font-bold text-ink-soft">
              When
            </label>
            <input
              id={`${formId}-when`}
              type="datetime-local"
              value={data.occurred_at}
              onChange={(event) => setData('occurred_at', event.target.value)}
              className="input-cute"
            />
            {!editing && (
              <button
                type="button"
                onClick={() => {
                  setData('occurred_at', '')
                  setPickingTime(false)
                }}
                className="mt-1.5 cursor-pointer text-sm font-bold text-blush-500 hover:underline"
              >
                Never mind — log it as right now
              </button>
            )}
          </>
        ) : (
          <button
            type="button"
            onClick={() => {
              setData('occurred_at', now)
              setPickingTime(true)
            }}
            className="cursor-pointer rounded-full border-2 border-blush-100 bg-white/80 px-4 py-2 font-display text-sm font-bold text-ink-soft transition hover:bg-white"
          >
            🕑 Happening now — tap to pick another time
          </button>
        )}
        <FieldError message={shown.occurred_at} />
      </div>

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={processing}
          className="btn-cute bg-blush-400 text-white shadow hover:bg-blush-500"
        >
          {editing ? 'Save changes' : feeding ? 'Log the feed' : 'Log the change'}
        </button>
        {editing && (
          <button type="button" onClick={onDone} className="btn-cute border-2 border-blush-100 bg-white text-ink-soft">
            Cancel
          </button>
        )}
      </div>
    </form>
  )
}

function Entry({ entry, onEdit }) {
  function remove() {
    if (!confirm(`Delete this ${entry.type === 'feeding' ? 'feed' : 'diaper change'}?`)) return
    router.delete(`${ENDPOINTS[entry.type]}/${entry.id}`, { preserveScroll: true })
  }

  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <span className="w-12 shrink-0 text-center text-xl" aria-hidden="true">
        {entry.emoji}
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate font-display font-extrabold text-ink">
          {entry.label}
          {entry.amountOz ? <span className="text-ink-soft"> · {entry.amountOz} oz</span> : null}
        </p>
        {entry.note && <p className="truncate text-sm text-ink-soft">{entry.note}</p>}
        <p className="truncate text-xs font-bold text-ink-soft/70">
          {entry.time} · {entry.loggedBy.name}
        </p>
      </div>

      <div className="flex shrink-0 gap-1">
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit the ${entry.label.toLowerCase()} at ${entry.time}`}
          className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full transition hover:bg-blush-50 sm:h-9 sm:w-9"
        >
          ✏️
        </button>
        <button
          type="button"
          onClick={remove}
          aria-label={`Delete the ${entry.label.toLowerCase()} at ${entry.time}`}
          className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full transition hover:bg-rose-50 sm:h-9 sm:w-9"
        >
          🗑️
        </button>
      </div>
    </li>
  )
}

// "2h 10m ago" — the number every bleary parent is actually after.
function useTimeSince(iso) {
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => setTick((count) => count + 1), 60_000)
    return () => clearInterval(timer)
  }, [])

  return useMemo(() => {
    if (!iso) return '—'

    const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
    if (minutes < 1) return 'just now'
    if (minutes < 60) return `${minutes}m ago`

    const hours = Math.floor(minutes / 60)
    if (hours < 24) return minutes % 60 === 0 ? `${hours}h ago` : `${hours}h ${minutes % 60}m ago`

    return `${Math.floor(hours / 24)}d ago`
  }, [iso, tick])
}

export default function CareLog({ entries, summary, now, feedingKinds, diaperKinds }) {
  const [editing, setEditing] = useState(null)
  // Which form last hit the server, so the shared error bag lands on the right one.
  const [activeForm, setActiveForm] = useState(null)
  const sinceLastFeed = useTimeSince(summary.lastFeedingAt)

  useLiveRefresh(REFRESHED_PROPS)

  const days = useMemo(() => {
    const grouped = []
    entries.forEach((entry) => {
      const last = grouped[grouped.length - 1]
      if (last && last.key === entry.dayKey) {
        last.entries.push(entry)
      } else {
        grouped.push({ key: entry.dayKey, label: entry.dayLabel, entries: [entry] })
      }
    })
    return grouped
  }, [entries])

  function quickLog(type, kinds) {
    const feeding = type === 'feeding'
    return (
      <div className="card-cute p-5 sm:p-6">
        <h2 className="mb-4 font-display text-xl font-extrabold text-ink">
          {feeding ? '🍼 Log a feed' : '🧷 Log a diaper change'}
        </h2>
        <EntryForm
          type={type}
          kinds={kinds}
          entry={null}
          now={now}
          formId={`new-${type}`}
          activeForm={activeForm}
          onSubmitting={setActiveForm}
          onDone={() => setActiveForm(null)}
        />
      </div>
    )
  }

  return (
    <Layout>
      <Head title="Feeds &amp; diapers" />

      <div className="mb-6">
        <h1 className="font-display text-3xl font-extrabold text-ink sm:text-4xl">Feeds &amp; diapers 🍼</h1>
        <p className="mt-1 text-ink-soft sm:mt-2 sm:text-lg">
          The nightshift notebook — what went in, what came out.
        </p>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
        <Stat emoji="🍼" value={summary.feedings} label="Feeds today" />
        <Stat emoji="🥛" value={summary.ounces ? `${summary.ounces} oz` : '—'} label="Milk today" />
        <Stat emoji="🧷" value={summary.diapers} label={`${summary.wet} wet · ${summary.dirty} dirty`} />
        <Stat emoji="🕑" value={sinceLastFeed} label="Since last feed" />
      </div>

      <div className="mb-8 grid gap-4 lg:grid-cols-2">
        {quickLog('feeding', feedingKinds)}
        {quickLog('diaper', diaperKinds)}
      </div>

      {days.length === 0 ? (
        <div className="card-cute p-10 text-center">
          <p className="text-5xl" aria-hidden="true">
            📖
          </p>
          <p className="mt-3 font-display text-xl font-extrabold text-ink">Nothing written down yet</p>
          <p className="mt-1 text-ink-soft">Log the next feed or change and it&rsquo;ll show up here.</p>
        </div>
      ) : (
        days.map((day) => (
          <section key={day.key} className="mb-6">
            <h2 className="mb-2 font-display text-lg font-extrabold text-ink-soft">{day.label}</h2>
            <ul className="card-cute divide-y-2 divide-blush-50 overflow-hidden">
              {day.entries.map((entry) =>
                editing && editing.type === entry.type && editing.id === entry.id ? (
                  <li key={`${entry.type}-${entry.id}`} className="animate-pop-in bg-blush-50/60 p-4">
                    <EntryForm
                      type={entry.type}
                      kinds={entry.type === 'feeding' ? feedingKinds : diaperKinds}
                      entry={entry}
                      now={now}
                      formId={`edit-${entry.type}-${entry.id}`}
                      activeForm={activeForm}
                      onSubmitting={setActiveForm}
                      onDone={() => {
                        setActiveForm(null)
                        setEditing(null)
                      }}
                    />
                  </li>
                ) : (
                  <Entry
                    key={`${entry.type}-${entry.id}`}
                    entry={entry}
                    onEdit={() => setEditing({ type: entry.type, id: entry.id })}
                  />
                ),
              )}
            </ul>
          </section>
        ))
      )}
    </Layout>
  )
}
